/* eslint-disable arrow-body-style */
import * as React from "react";
import {observer} from "mobx-react";
import {MapState} from "../../../../core/map-state";
import {AppState} from "../../app-mobx";
import {Auftragsverwaltung} from "../../../../stores/auftragsverwaltung-mobx";
import {autorun} from "mobx";
import FeatureFilterProperties = __esri.FeatureFilterProperties;
import {MapViewerConfiguration} from "../../../../config/map-viewer-configuration";
import {Project} from "../../../../models/project";


export interface LayerProjectFilterProps {
    mapStore: MapState;
    appStore: AppState;
    auftragsverwaltung: Auftragsverwaltung;
    mapViewerConfig: MapViewerConfiguration;
}

/**
 * Filter features belonging to selected project
 */
@observer
export class LayerProjectFilter extends React.Component<LayerProjectFilterProps, any> {
    autorunDisposers: any[] = [];
    constructor(props: LayerProjectFilterProps) {
        super(props);
        this.state = {

        };
    }

    render() {
        return null;
    }

    componentDidMount(): void {
        this.autorunDisposers.push(autorun(() => {
            const {
                auftragsverwaltung,
                mapStore
            } = this.props;

            if (!mapStore.map || !mapStore.view || !auftragsverwaltung.showMap) {
                return;
            }

            const query = this.prepareQuery(auftragsverwaltung.selectedProject);
            if (!query || !query["where"]) {
                return;
            }

            // get layers that have to be filtered
            const filterableLayers = auftragsverwaltung.filterableLayers;
            if (filterableLayers.length === 0) {
                return;
            }

            this.applyFilterAndZoom(filterableLayers, query);
        }));
    }

    componentWillUnmount() {
        this.autorunDisposers.forEach((dispose: any) => {
            dispose();
        });
        this.autorunDisposers.length = 0;
    }

    /**
     * Combine filter based on selected object and object type
     */
    private prepareQuery(selectedProject: Project | null): object | null {
        if (!selectedProject) {
            return { where: "1 = 1" };
        }

        // TODO: Attributnamen in config auslagern
        return { where: `PROJ_ID = ${selectedProject.id}` };
    }

    private applyFilterAndZoom(filterableLayers, query): void {
        const {
            appStore,
            mapStore,
            mapViewerConfig
        } = this.props;
        const apiConfig = mapViewerConfig.auftragsverwaltungApi;

        const extentPromises: Array<Promise<any>> = [];
        const filterPromises: Array<Promise<any>> = [];
        filterableLayers.forEach((fl: __esri.FeatureLayer) => {
            filterPromises.push(new Promise((resolveFilter, rejectFilter) => {
                mapStore.view?.whenLayerView(fl)
                    .then((layerView) => {
                        const featureLayerView = layerView as __esri.FeatureLayerViewProperties;

                        // set visibility of layers based on current topic
                        const layerConfig = apiConfig.getLayerConfigByPortalId(fl.portalItem.id, fl.layerId);
                        if (!layerConfig || !layerConfig.topicIds || layerConfig.topicIds.length === 0) {
                            console.warn("Layer project filter: Topic ids not defined on layer config. Filter not applicable.");
                            return;
                        }
                        featureLayerView.visible = !!layerConfig?.topicIds.includes(appStore.topic?.id);

                        featureLayerView.filter = query as unknown as FeatureFilterProperties;

                        // get extent of all features in the view
                        extentPromises.push(new Promise((resolve, reject) => {
                            // queryExtent on layerView does not work --> pass filter as query to layer to
                            // get the same result
                            const featureQuery = fl.createQuery();
                            featureQuery.returnGeometry = true;
                            featureQuery.where = `${featureQuery.where} AND ${query["where"]}`;

                            fl.queryFeatures(featureQuery).then((result) => {
                                if (result && result.features) {
                                    resolve(result.features);
                                } else {
                                    reject();
                                }
                            }).catch((error) => {
                                console.debug(error);
                                // no features left, ignore error
                                resolve(null);
                            });
                        }));
                        resolveFilter(null);
                    }, (error) => {
                        rejectFilter(error);
                    });
            }));
        });

        // wait until all filters are applied to all feature layers
        Promise.all(filterPromises).then(() => {
            // wait for all extent promises to complete and from all filtered layers
            // zoom to extent off all features present after the filter was applied
            Promise.all(extentPromises).then((features) => {
                if (!features || features.length === 0) {
                    return;
                }
                // filter null extents
                // eslint-disable-next-line prefer-spread
                const allFeatures = [].concat.apply([], features).filter((x: any) => !!x && !!x.geometry);
                if (!allFeatures || allFeatures.length === 0) {
                    return;
                }

                const ext = allFeatures[0].geometry.extent;
                for (let i = 1; i < allFeatures.length; i++) {
                    if (allFeatures[i]) {
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        ext.union(allFeatures[i].geometry.extent);
                    }
                }
                if (ext) {
                    mapStore.view?.goTo(ext);
                }
            });
        });
    }
}
